import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Plus, 
  Search, 
  FileText,
  Calendar as CalendarIcon,
  Loader2,
  MoreVertical,
  Eye,
  Send,
  CheckCircle,
  Trash2,
  DollarSign,
  Download,
  CreditCard,
  Receipt,
  Pencil,
  Mail,
  MailCheck
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' }
];

const Invoices = () => {
  const { user, getAuthHeaders } = useAuth();
  const [platformInvoices, setPlatformInvoices] = useState([]);  // Bills TO tenant (from Mel)
  const [tenantInvoices, setTenantInvoices] = useState([]);      // Bills FROM tenant to their clients
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('bills');  // 'bills' or 'client-invoices'
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [editDueDate, setEditDueDate] = useState(null);
  const [items, setItems] = useState([{ description: '', quantity: 1, rate: 0, amount: 0 }]);
  const [editItems, setEditItems] = useState([]);
  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    organization_id: '',
    notes: '',
    invoice_type: 'tenant'  // Tenant creates invoices for their clients
  });
  const [editFormData, setEditFormData] = useState({
    client_id: '',
    project_id: '',
    notes: '',
    status: ''
  });

  const isSuperAdmin = user?.role === 'super_admin';
  const isTenantUser = user?.role === 'admin' || user?.role === 'team';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/invoices?invoice_type=platform`, getAuthHeaders()),  // Bills to pay
        axios.get(`${API_URL}/clients`, getAuthHeaders()),
        axios.get(`${API_URL}/projects`, getAuthHeaders())
      ];
      
      // If tenant user, also fetch their client invoices
      if (isTenantUser) {
        requests.push(axios.get(`${API_URL}/invoices?invoice_type=tenant`, getAuthHeaders()));
      }
      
      const responses = await Promise.all(requests);
      setPlatformInvoices(responses[0].data);
      setClients(responses[1].data);
      setProjects(responses[2].data);
      
      if (isTenantUser && responses[3]) {
        setTenantInvoices(responses[3].data);
      }

      if (isSuperAdmin) {
        const tenantsRes = await axios.get(`${API_URL}/tenants`, getAuthHeaders());
        setOrganizations(tenantsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        items: items.map(i => ({ ...i, amount: i.quantity * i.rate })),
        due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        organization_id: isSuperAdmin ? formData.organization_id : user.organization_id,
        invoice_type: isSuperAdmin ? 'platform' : 'tenant'  // Super admin creates platform invoices, tenants create client invoices
      };
      
      await axios.post(`${API_URL}/invoices`, data, getAuthHeaders());
      toast.success('Invoice created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleStatusUpdate = async (invoiceId, status) => {
    try {
      await axios.put(`${API_URL}/invoices/${invoiceId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success('Invoice status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoiceNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/invoices/${invoiceId}`, getAuthHeaders());
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoiceId}/pdf`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleSendInvoiceEmail = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.post(`${API_URL}/invoices/${invoiceId}/send`, {}, getAuthHeaders());
      toast.success(`Invoice ${invoiceNumber} sent to ${response.data.recipient}`);
      // Refresh invoices to show updated email_sent status
      fetchInvoices();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send invoice email';
      toast.error(errorMsg);
    }
  };

  const openEditModal = (invoice) => {
    setSelectedInvoice(invoice);
    setEditFormData({
      client_id: invoice.client_id || '',
      project_id: invoice.project_id || '',
      notes: invoice.notes || '',
      status: invoice.status || 'draft'
    });
    setEditItems(invoice.items?.map(i => ({ ...i })) || [{ description: '', quantity: 1, rate: 0, amount: 0 }]);
    setEditDueDate(invoice.due_date ? new Date(invoice.due_date) : null);
    setIsEditOpen(true);
  };

  const handleEditItemChange = (index, field, value) => {
    const newItems = [...editItems];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    setEditItems(newItems);
  };

  const addEditItem = () => {
    setEditItems([...editItems, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const removeEditItem = (index) => {
    if (editItems.length > 1) {
      setEditItems(editItems.filter((_, i) => i !== index));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        client_id: editFormData.client_id,
        project_id: editFormData.project_id || null,
        notes: editFormData.notes,
        status: editFormData.status,
        due_date: editDueDate ? format(editDueDate, 'yyyy-MM-dd') : null,
        items: editItems.map(i => ({ description: i.description, quantity: i.quantity, rate: i.rate, amount: i.quantity * i.rate }))
      };
      
      await axios.put(`${API_URL}/invoices/${selectedInvoice.id}`, data, getAuthHeaders());
      toast.success('Invoice updated successfully');
      setIsEditOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      project_id: '',
      organization_id: '',
      notes: ''
    });
    setItems([{ description: '', quantity: 1, rate: 0, amount: 0 }]);
    setDueDate(null);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown';
  };

  // Get current invoices based on active tab
  const currentInvoices = activeTab === 'bills' ? platformInvoices : tenantInvoices;

  const filteredInvoices = currentInvoices.filter(invoice => {
    const clientName = invoice.client_name || getClientName(invoice.client_id);
    const matchesSearch = invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats for current view
  const totalAmount = currentInvoices.reduce((acc, i) => acc + i.total, 0);
  const paidAmount = currentInvoices.filter(i => i.status === 'paid').reduce((acc, i) => acc + i.total, 0);
  const pendingAmount = currentInvoices.filter(i => ['sent', 'overdue'].includes(i.status)).reduce((acc, i) => acc + i.total, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Invoices
          </h1>
          <p className="text-slate-500 mt-1">
            {isTenantUser ? (activeTab === 'bills' ? 'Bills you need to pay' : 'Invoices to your clients') : 'Manage all invoices'}
          </p>
        </div>
        
        {/* Only show create button for tenant's client invoices or super admin */}
        {(isSuperAdmin || activeTab === 'client-invoices') && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-invoice-btn">
                <Plus size={18} className="mr-2" />
                New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {isSuperAdmin ? 'Create Invoice for Tenant' : 'Create Invoice for Client'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                {isSuperAdmin ? (
                  <>
                    {/* Super Admin: Bill TO Tenant */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Bill To (Tenant) *</Label>
                        <Select
                          value={formData.organization_id}
                          onValueChange={(value) => setFormData({ ...formData, organization_id: value, client_id: '', project_id: '' })}
                        >
                          <SelectTrigger data-testid="invoice-tenant-select">
                            <SelectValue placeholder="Select tenant" />
                          </SelectTrigger>
                          <SelectContent>
                            {organizations.map(org => (
                              <SelectItem key={org.id} value={org.id}>{org.company_name || org.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Project (Optional)</Label>
                        <Select
                          value={formData.project_id}
                          onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                          disabled={!formData.organization_id}
                        >
                          <SelectTrigger data-testid="invoice-project-select">
                            <SelectValue placeholder="Select project" />
                          </SelectTrigger>
                          <SelectContent>
                            {projects.filter(p => p.organization_id === formData.organization_id).map(project => (
                              <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Tenant: Bill TO their Client */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Bill To (Client) *</Label>
                        <Select
                          value={formData.client_id}
                          onValueChange={(value) => setFormData({ ...formData, client_id: value, project_id: '' })}
                        >
                          <SelectTrigger data-testid="invoice-client-select">
                            <SelectValue placeholder="Select client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map(client => (
                              <SelectItem key={client.id} value={client.id}>{client.company || client.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Project (Optional)</Label>
                        <Select
                          value={formData.project_id}
                          onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                          disabled={!formData.client_id}
                        >
                          <SelectTrigger data-testid="invoice-project-select">
                            <SelectValue placeholder="Select project" />
                          </SelectTrigger>
                          <SelectContent>
                            {projects.filter(p => p.client_id === formData.client_id).map(project => (
                              <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}

              <div className="space-y-2">
                <Label>Due Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="invoice-due-date-btn">
                      <CalendarIcon size={16} className="mr-2" />
                      {dueDate ? format(dueDate, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                    <Plus size={14} className="mr-1" /> Add Item
                  </Button>
                </div>
                
                {items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-3 rounded-lg">
                    <div className="col-span-5">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Service description"
                        required
                        data-testid={`item-description-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        data-testid={`item-qty-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Rate ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={item.rate}
                        onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                        data-testid={`item-rate-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Amount</Label>
                      <p className="h-10 flex items-center font-medium text-slate-900">
                        ${(item.quantity * item.rate).toFixed(2)}
                      </p>
                    </div>
                    <div className="col-span-1">
                      {items.length > 1 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon" 
                          className="h-10 text-red-500 hover:text-red-700"
                          onClick={() => removeItem(index)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex justify-end pt-2">
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Total</p>
                    <p className="text-2xl font-bold text-slate-900">
                      ${items.reduce((acc, i) => acc + (i.quantity * i.rate), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes for the invoice..."
                  rows={2}
                  data-testid="invoice-notes-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-invoice-btn">
                  Create Invoice
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                <DollarSign size={24} className="text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${totalAmount.toLocaleString()}</p>
                <p className="text-sm text-slate-500">{activeTab === 'bills' ? 'Total Owed' : 'Total Invoiced'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CheckCircle size={24} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${paidAmount.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
                <FileText size={24} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${pendingAmount.toLocaleString()}</p>
                <p className="text-sm text-slate-500">{activeTab === 'bills' ? 'Balance Due' : 'Pending'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Tenant Users */}
      {isTenantUser && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="bills" className="data-[state=active]:bg-white flex items-center gap-2">
              <CreditCard size={16} />
              Bills to Pay
              {platformInvoices.filter(i => ['sent', 'overdue'].includes(i.status)).length > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                  {platformInvoices.filter(i => ['sent', 'overdue'].includes(i.status)).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="client-invoices" className="data-[state=active]:bg-white flex items-center gap-2">
              <Receipt size={16} />
              My Client Invoices
              <span className="ml-1 px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full">
                {tenantInvoices.length}
              </span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-invoices-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="invoice-status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statusOptions.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {filteredInvoices.length === 0 ? (
            <div className="p-12 text-center">
              <FileText size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No invoices found</h3>
              <p className="text-slate-500 mt-1">Create your first invoice to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Bill To</th>
                    <th>Amount</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                      <td className="font-medium text-slate-900">{invoice.invoice_number}</td>
                      <td>
                        {invoice.invoice_type === 'platform' 
                          ? (invoice.organization_name || 'Unknown Tenant')
                          : (invoice.client_name || getClientName(invoice.client_id))
                        }
                      </td>
                      <td className="font-medium">${invoice.total.toLocaleString()}</td>
                      <td>{format(new Date(invoice.due_date), 'MMM d, yyyy')}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className={`status-badge invoice-${invoice.status}`}>
                            {statusOptions.find(s => s.value === invoice.status)?.label}
                          </span>
                          {invoice.email_sent && (
                            <span title={`Emailed on ${invoice.email_sent_at ? format(new Date(invoice.email_sent_at), 'MMM d, yyyy') : 'N/A'}`}>
                              <MailCheck size={14} className="text-green-500" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setSelectedInvoice(invoice); setIsViewOpen(true); }}>
                              <Eye size={14} className="mr-2" />
                              View
                            </DropdownMenuItem>
                            {user?.role === 'super_admin' && (
                              <DropdownMenuItem onClick={() => openEditModal(invoice)}>
                                <Pencil size={14} className="mr-2" />
                                Edit Invoice
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)}>
                              <Download size={14} className="mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            {user?.role === 'super_admin' && (
                              <DropdownMenuItem 
                                onClick={() => handleSendInvoiceEmail(invoice.id, invoice.invoice_number)}
                                className="text-blue-600 focus:text-blue-600"
                              >
                                <Send size={14} className="mr-2" />
                                Send Invoice Email
                              </DropdownMenuItem>
                            )}
                            {invoice.status === 'draft' && user?.role === 'super_admin' && (
                              <DropdownMenuItem onClick={() => handleStatusUpdate(invoice.id, 'sent')}>
                                <CheckCircle size={14} className="mr-2" />
                                Mark as Sent
                              </DropdownMenuItem>
                            )}
                            {['sent', 'overdue'].includes(invoice.status) && user?.role === 'super_admin' && (
                              <DropdownMenuItem onClick={() => handleStatusUpdate(invoice.id, 'paid')}>
                                <CheckCircle size={14} className="mr-2" />
                                Mark as Paid
                              </DropdownMenuItem>
                            )}
                            {user?.role === 'super_admin' && (
                              <DropdownMenuItem 
                                onClick={() => handleDeleteInvoice(invoice.id, invoice.invoice_number)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 size={14} className="mr-2" />
                                Delete Invoice
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Invoice Modal */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Invoice {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6 mt-4">
              {/* From / To Section - Different layout based on invoice type */}
              <div className="grid grid-cols-2 gap-6 p-4 bg-slate-50 rounded-lg">
                {selectedInvoice.invoice_type === 'tenant' ? (
                  <>
                    {/* Tenant Invoice: FROM tenant TO their client */}
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">From</p>
                      <p className="font-semibold text-slate-900">{selectedInvoice.organization_name || 'Unknown'}</p>
                      <p className="text-sm text-slate-600">{selectedInvoice.organization_contact || ''}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Bill To</p>
                      <p className="font-semibold text-slate-900">{selectedInvoice.client_name || getClientName(selectedInvoice.client_id)}</p>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Platform Invoice: FROM a2g designs TO tenant */}
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">From</p>
                      <img src="https://customer-assets.emergentagent.com/job_agency-command-16/artifacts/jeoqwcw6_Web_Design_Tampa-250-2018-LIGHT%20-%20Copy.png" alt="a2g designs" className="h-12 mb-2" />
                      <p className="text-sm text-slate-600">{selectedInvoice.created_by_name || 'Mel Tallagsen - Software Engineer'}</p>
                      <p className="text-sm text-slate-500">mel@a2gdesigns.com</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Bill To</p>
                      <p className="font-semibold text-slate-900">{selectedInvoice.organization_name || 'Unknown'}</p>
                      <p className="text-sm text-slate-600">{selectedInvoice.organization_contact || ''}</p>
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <span className={`status-badge invoice-${selectedInvoice.status}`}>
                    {statusOptions.find(s => s.value === selectedInvoice.status)?.label}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Due Date</p>
                  <p className="font-medium">{format(new Date(selectedInvoice.due_date), 'MMM d, yyyy')}</p>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-slate-700">Description</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-700">Qty</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-700">Rate</th>
                      <th className="text-right p-3 text-sm font-medium text-slate-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items?.map((item, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-3">{item.description}</td>
                        <td className="p-3 text-right">{item.quantity}</td>
                        <td className="p-3 text-right">${item.rate.toFixed(2)}</td>
                        <td className="p-3 text-right font-medium">${item.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t">
                    <tr>
                      <td colSpan="3" className="p-3 text-right font-medium">Total</td>
                      <td className="p-3 text-right font-bold text-lg">${selectedInvoice.total.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedInvoice.notes && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Notes</p>
                  <p className="text-slate-700">{selectedInvoice.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Edit Invoice {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select
                  value={editFormData.client_id}
                  onValueChange={(value) => setEditFormData({ ...editFormData, client_id: value })}
                >
                  <SelectTrigger data-testid="edit-invoice-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editFormData.status}
                  onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}
                >
                  <SelectTrigger data-testid="edit-invoice-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project (Optional)</Label>
                <Select
                  value={editFormData.project_id}
                  onValueChange={(value) => setEditFormData({ ...editFormData, project_id: value })}
                >
                  <SelectTrigger data-testid="edit-invoice-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.filter(p => p.client_id === editFormData.client_id).map(project => (
                      <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="edit-invoice-due-date-btn">
                      <CalendarIcon size={16} className="mr-2" />
                      {editDueDate ? format(editDueDate, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={editDueDate} onSelect={setEditDueDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addEditItem} data-testid="edit-add-item-btn">
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
              
              {editItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-3 rounded-lg">
                  <div className="col-span-5">
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => handleEditItemChange(index, 'description', e.target.value)}
                      placeholder="Service description"
                      required
                      data-testid={`edit-item-description-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleEditItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      data-testid={`edit-item-qty-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Rate ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={item.rate}
                      onChange={(e) => handleEditItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                      data-testid={`edit-item-rate-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Amount</Label>
                    <p className="h-10 flex items-center font-medium text-slate-900">
                      ${(item.quantity * item.rate).toFixed(2)}
                    </p>
                  </div>
                  <div className="col-span-1">
                    {editItems.length > 1 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon" 
                        className="h-10 text-red-500 hover:text-red-700"
                        onClick={() => removeEditItem(index)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-end pt-2">
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${editItems.reduce((acc, i) => acc + (i.quantity * i.rate), 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="Additional notes for the invoice..."
                rows={2}
                data-testid="edit-invoice-notes-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="update-invoice-btn">
                Update Invoice
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
